import { useEffect, useState } from 'react';
import { CircleHelp, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/button';
import {
    createHomeOnboardingSteps,
    type OnboardingStep,
} from './onboardingSteps';
import {
    getOnboardingWizardState,
    updateOnboardingWizardState,
    type UpdateOnboardingWizardInput,
} from '../../services/api/onboardingWizardService';
import {
    EVENT_KEYS,
    trackClick,
    trackLoad,
} from '../../services/api/trackingService';
import logo from '../../images/LogohvidFinal.png';

interface SpotlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

const SPOTLIGHT_PADDING = 10;
const CARD_GAP = 16;
const CARD_SURFACE_CLASS =
    'relative rounded-lg border border-white/10 bg-[linear-gradient(135deg,var(--main-color-dark)_0%,#263927_58%,#2a2a2a_100%)] text-white shadow-[0_24px_80px_rgba(0,0,0,0.45)]';
const BODY_TEXT_CLASS = 'mt-3 text-sm leading-6 text-white/80';
type WizardOpenSource = 'auto' | 'help_button' | 'unknown';

export default function OnboardingWizard() {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [openSource, setOpenSource] =
        useState<WizardOpenSource>('unknown');
    const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(
        null
    );
    const [spotlightTargetMissing, setSpotlightTargetMissing] =
        useState(false);

    const steps = createHomeOnboardingSteps();
    const currentStep = steps[currentStepIndex];
    const isLastStep = currentStepIndex === steps.length - 1;

    useEffect(() => {
        let isMounted = true;

        getOnboardingWizardState()
            .then((wizardState) => {
                if (!isMounted) return;

                const savedStepIndex = clampStepIndex(
                    wizardState.onboardingWizardCurrentStep - 1,
                    steps.length
                );
                setCurrentStepIndex(savedStepIndex);

                if (
                    wizardState.isNewUserForOnboarding &&
                    !wizardState.onboardingWizardFirstShownAt
                ) {
                    setCurrentStepIndex(0);
                    setOpenSource('auto');
                    setIsOpen(true);
                    saveWizardState({
                        action: 'auto_opened',
                        currentStep: 1,
                    });
                    trackWizardOpen('auto', 0);
                    trackWizardStep(0, 'auto');
                }
            })
            .catch((): void => undefined);

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!isOpen || currentStep.type !== 'spotlight') {
            setSpotlightRect(null);
            setSpotlightTargetMissing(false);
            return;
        }

        const updateSpotlightRect = () => {
            const target = getStepTarget(currentStep);
            if (!target) {
                setSpotlightRect(null);
                setSpotlightTargetMissing(true);
                return;
            }

            const rect = target.getBoundingClientRect();
            setSpotlightTargetMissing(false);
            setSpotlightRect({
                top: Math.max(rect.top - SPOTLIGHT_PADDING, SPOTLIGHT_PADDING),
                left: Math.max(
                    rect.left - SPOTLIGHT_PADDING,
                    SPOTLIGHT_PADDING
                ),
                width: rect.width + SPOTLIGHT_PADDING * 2,
                height: rect.height + SPOTLIGHT_PADDING * 2,
            });
        };

        updateSpotlightRect();
        window.addEventListener('resize', updateSpotlightRect);
        window.addEventListener('scroll', updateSpotlightRect, true);

        return () => {
            window.removeEventListener('resize', updateSpotlightRect);
            window.removeEventListener('scroll', updateSpotlightRect, true);
        };
    }, [currentStepIndex, currentStep.type, isOpen]);

    const saveWizardState = (input: UpdateOnboardingWizardInput) => {
        updateOnboardingWizardState(input).catch((): void => undefined);
    };
    const getStep = (stepIndex: number) =>
        steps[clampStepIndex(stepIndex, steps.length)];
    const trackWizardOpen = (
        source: WizardOpenSource,
        stepIndex: number
    ) => {
        trackLoad(EVENT_KEYS.onboardingWizardOpen, {
            source,
            currentStep: stepIndex + 1,
            totalSteps: steps.length,
        });
    };
    const trackWizardStep = (
        stepIndex: number,
        source: WizardOpenSource = openSource
    ) => {
        const step = getStep(stepIndex);
        trackLoad(EVENT_KEYS.onboardingWizardStep, {
            stepIndex: stepIndex + 1,
            stepKey: step.id,
            stepTitle: step.title,
            totalSteps: steps.length,
            source,
        });
    };
    const trackWizardNavigation = (
        eventType:
            | EVENT_KEYS.onboardingWizardNext
            | EVENT_KEYS.onboardingWizardBack,
        fromStepIndex: number,
        toStepIndex: number
    ) => {
        const fromStep = getStep(fromStepIndex);
        const toStep = getStep(toStepIndex);
        trackClick(eventType, {
            fromStepIndex: fromStepIndex + 1,
            toStepIndex: toStepIndex + 1,
            fromStepKey: fromStep.id,
            toStepKey: toStep.id,
            totalSteps: steps.length,
        });
    };
    const dismissOnboarding = () => {
        trackClick(EVENT_KEYS.onboardingWizardClose, {
            stepIndex: currentStepIndex + 1,
            stepKey: currentStep.id,
            totalSteps: steps.length,
            reason: 'dismissed',
        });
        saveWizardState({
            action: 'dismissed',
            currentStep: currentStepIndex + 1,
        });
        setIsOpen(false);
    };
    const completeOnboarding = () => {
        trackClick(EVENT_KEYS.onboardingWizardFinish, {
            stepIndex: currentStepIndex + 1,
            stepKey: currentStep.id,
            totalSteps: steps.length,
            reason: 'completed',
        });
        saveWizardState({
            action: 'completed',
            currentStep: currentStepIndex + 1,
        });
        setIsOpen(false);
    };
    const goToStep = (stepIndex: number) => {
        const nextStepIndex = clampStepIndex(stepIndex, steps.length);
        setSpotlightTargetMissing(false);
        setCurrentStepIndex(nextStepIndex);
        saveWizardState({
            action: 'step_changed',
            currentStep: nextStepIndex + 1,
        });
        trackWizardStep(nextStepIndex);
    };
    const nextStep = () => {
        if (currentStepIndex < steps.length - 1) {
            const nextStepIndex = currentStepIndex + 1;
            trackWizardNavigation(
                EVENT_KEYS.onboardingWizardNext,
                currentStepIndex,
                nextStepIndex
            );
            goToStep(nextStepIndex);
        } else {
            completeOnboarding();
        }
    };
    const previousStep = () => {
        if (currentStepIndex > 0) {
            const previousStepIndex = currentStepIndex - 1;
            trackWizardNavigation(
                EVENT_KEYS.onboardingWizardBack,
                currentStepIndex,
                previousStepIndex
            );
            goToStep(previousStepIndex);
        }
    };
    const reopenOnboarding = () => {
        trackClick(EVENT_KEYS.onboardingHelpButtonClick, {
            location: 'homepage',
            wizardAvailable: true,
        });
        setCurrentStepIndex(0);
        setOpenSource('help_button');
        setIsOpen(true);
        saveWizardState({ action: 'opened', currentStep: 1 });
        trackWizardOpen('help_button', 0);
        trackWizardStep(0, 'help_button');
    };

    return (
        <>
            {isOpen && currentStep.type === 'welcome' && (
                <WelcomeStep
                    step={currentStep}
                    isFirstStep={currentStepIndex === 0}
                    isLastStep={isLastStep}
                    currentStepNumber={currentStepIndex + 1}
                    totalSteps={steps.length}
                    onClose={dismissOnboarding}
                    onBack={previousStep}
                    onNext={nextStep}
                />
            )}

            {isOpen && currentStep.type === 'spotlight' && spotlightRect && (
                <SpotlightStep
                    step={currentStep}
                    rect={spotlightRect}
                    isFirstStep={currentStepIndex === 0}
                    isLastStep={isLastStep}
                    currentStepNumber={currentStepIndex + 1}
                    totalSteps={steps.length}
                    onClose={dismissOnboarding}
                    onBack={previousStep}
                    onNext={nextStep}
                />
            )}

            {isOpen &&
                currentStep.type === 'spotlight' &&
                !spotlightRect &&
                spotlightTargetMissing && (
                    <WelcomeStep
                        step={currentStep}
                        isFirstStep={currentStepIndex === 0}
                        isLastStep={isLastStep}
                        currentStepNumber={currentStepIndex + 1}
                        totalSteps={steps.length}
                        onClose={dismissOnboarding}
                        onBack={previousStep}
                        onNext={nextStep}
                    />
                )}

            {!isOpen && (
                <Button
                    type="button"
                    size="icon"
                    className="fixed bottom-28 right-8 z-40 h-12 w-12 rounded-full bg-[#2f2f2f] text-white shadow-lg hover:bg-[#3a3a3a]"
                    aria-label={t('home.onboarding.reopen', {
                        defaultValue: 'Open onboarding',
                    })}
                    onClick={reopenOnboarding}
                >
                    <CircleHelp className="h-5 w-5" aria-hidden="true" />
                </Button>
            )}
        </>
    );
}

function WelcomeStep({
    step,
    isFirstStep,
    isLastStep,
    currentStepNumber,
    totalSteps,
    onClose,
    onBack,
    onNext,
}: {
    step: OnboardingStep;
    isFirstStep: boolean;
    isLastStep: boolean;
    currentStepNumber: number;
    totalSteps: number;
    onClose: () => void;
    onBack: () => void;
    onNext: () => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
            <div className={`${CARD_SURFACE_CLASS} w-full max-w-md p-5`}>
                <CloseButton onClose={onClose} />
                <StepHeader step={step} titleClassName="text-xl" />
                <p className={BODY_TEXT_CLASS}>
                    {t(step.descriptionKey, {
                        defaultValue: step.description,
                    })}
                </p>
                <StepActions
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    currentStepNumber={currentStepNumber}
                    totalSteps={totalSteps}
                    onBack={onBack}
                    onNext={onNext}
                />
            </div>
        </div>
    );
}

function SpotlightStep({
    step,
    rect,
    isFirstStep,
    isLastStep,
    currentStepNumber,
    totalSteps,
    onClose,
    onBack,
    onNext,
}: {
    step: OnboardingStep;
    rect: SpotlightRect;
    isFirstStep: boolean;
    isLastStep: boolean;
    currentStepNumber: number;
    totalSteps: number;
    onClose: () => void;
    onBack: () => void;
    onNext: () => void;
}) {
    const { t } = useTranslation();
    const cardPosition = getCardPosition(rect);

    return (
        <div className="fixed inset-0 z-50 pointer-events-none">
            <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
                <defs>
                    <mask id="implant-search-spotlight-mask">
                        <rect width="100%" height="100%" fill="white" />
                        <rect
                            x={rect.left}
                            y={rect.top}
                            width={rect.width}
                            height={rect.height}
                            rx="32"
                            fill="black"
                        />
                    </mask>
                </defs>
                <rect
                    width="100%"
                    height="100%"
                    fill="rgba(0, 0, 0, 0.72)"
                    mask="url(#implant-search-spotlight-mask)"
                />
            </svg>

            <div
                className={`${CARD_SURFACE_CLASS} pointer-events-auto fixed w-[calc(100vw-2rem)] max-w-sm p-4`}
                style={{
                    top: cardPosition.top,
                    left: cardPosition.left,
                }}
            >
                <CloseButton onClose={onClose} />
                <StepHeader step={step} titleClassName="text-lg" />
                <p className={BODY_TEXT_CLASS}>
                    {t(step.descriptionKey, {
                        defaultValue: step.description,
                    })}
                </p>
                <StepActions
                    isFirstStep={isFirstStep}
                    isLastStep={isLastStep}
                    currentStepNumber={currentStepNumber}
                    totalSteps={totalSteps}
                    onBack={onBack}
                    onNext={onNext}
                />
            </div>
        </div>
    );
}

function StepHeader({
    step,
    titleClassName,
}: {
    step: OnboardingStep;
    titleClassName: string;
}) {
    const { t } = useTranslation();

    return (
        <div className="pr-8">
            <img src={logo} alt="NordInsight" className="mb-4 h-6 w-auto" />
            <h3 className={`${titleClassName} font-semibold tracking-normal`}>
                {t(step.titleKey, { defaultValue: step.title })}
            </h3>
        </div>
    );
}

function StepActions({
    isFirstStep,
    isLastStep,
    currentStepNumber,
    totalSteps,
    onBack,
    onNext,
}: {
    isFirstStep: boolean;
    isLastStep: boolean;
    currentStepNumber: number;
    totalSteps: number;
    onBack: () => void;
    onNext: () => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="mt-5 space-y-4">
            <StepProgress
                currentStepNumber={currentStepNumber}
                totalSteps={totalSteps}
            />
            <div className="flex justify-between gap-3">
                {!isFirstStep ? (
                    <Button size="sm" variant="ghost" onClick={onBack}>
                        {t('home.onboarding.back', {
                            defaultValue: 'Back',
                        })}
                    </Button>
                ) : (
                    <span />
                )}
                <Button size="sm" onClick={onNext}>
                    {isLastStep
                        ? t('home.onboarding.finish', {
                              defaultValue: 'Finish',
                          })
                        : t('home.onboarding.next', {
                              defaultValue: 'Next',
                          })}
                </Button>
            </div>
        </div>
    );
}

function StepProgress({
    currentStepNumber,
    totalSteps,
}: {
    currentStepNumber: number;
    totalSteps: number;
}) {
    const { t } = useTranslation();

    return (
        <div className="flex items-center justify-between gap-4 border-t border-white/10 pt-4">
            <span className="text-xs font-medium text-white/65">
                {t('home.onboarding.progress', {
                    currentStepNumber,
                    totalSteps,
                    defaultValue: 'Step {{currentStepNumber}} of {{totalSteps}}',
                })}
            </span>
            <div className="flex items-center gap-1.5" aria-hidden="true">
                {Array.from({ length: totalSteps }, (_, index) => {
                    const isFilled = index < currentStepNumber;
                    return (
                        <span
                            key={index}
                            className={`h-1.5 w-1.5 rounded-full transition-colors ${
                                isFilled ? 'bg-white' : 'bg-white/25'
                            }`}
                        />
                    );
                })}
            </div>
        </div>
    );
}

function CloseButton({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation();

    return (
        <button
            type="button"
            className="absolute right-2 top-2 rounded-md p-1 text-white/70 hover:bg-white/10 hover:text-white"
            aria-label={t('common.close', {
                defaultValue: 'Close',
            })}
            onClick={onClose}
        >
            <X className="h-4 w-4" aria-hidden="true" />
        </button>
    );
}

function getCardPosition(rect: SpotlightRect) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const cardWidth = Math.min(384, viewportWidth - 32);
    const estimatedCardHeight = 190;
    const hasRoomBelow =
        rect.top + rect.height + CARD_GAP + estimatedCardHeight <
        viewportHeight;

    const top = hasRoomBelow
        ? rect.top + rect.height + CARD_GAP
        : Math.max(16, rect.top - estimatedCardHeight - CARD_GAP);
    const left = Math.min(
        Math.max(16, rect.left + rect.width / 2 - cardWidth / 2),
        viewportWidth - cardWidth - 16
    );

    return { top, left };
}

function getStepTarget(step: OnboardingStep): HTMLElement | null {
    if (step.targetSelector) {
        return document.querySelector<HTMLElement>(step.targetSelector);
    }
    return null;
}

function clampStepIndex(stepIndex: number, totalSteps: number) {
    return Math.min(Math.max(stepIndex, 0), totalSteps - 1);
}
